import { Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
//Alert do lab, e não o MyAlert do projeto: aquele é o toast global via redux
import Alert from '@material-ui/lab/Alert';
import AlertTitle from '@material-ui/lab/AlertTitle';
import WarningIcon from '@material-ui/icons/Warning';
import React, { useRef, useState } from 'react';
import { useDispatch } from 'react-redux';

import allActions from '../../../../../../actions';
import { TrademateAPI } from '../../../../../../API/trademate';
import { unoTheme } from '../../../../../../assets/styles/unoTheme';
import { isDocumentFulfilled } from './documentUtils';
import TrademateComplementaryDocumentRow from './TrademateComplementaryDocumentRow';
import TrademateDocumentRow from './TrademateDocumentRow';

const useStyles = makeStyles({
    //O MUI não tem severity="primary" (são error/warning/info/success), então parte-se do `info`,
    //que traz o ícone certo, e recoloca-se o azul da marca por cima do azul padrão do MUI.
    instructions: {
        marginBottom: '16px',
        color: unoTheme.defaultTextColor,
        backgroundColor: unoTheme.secondPrimaryButtonColor,

        '& .MuiAlert-icon': {
            color: unoTheme.mainColor,
        },
        '& .MuiAlert-message': {
            fontSize: '12px',
        },
    },
    instructionsTitle: {
        fontSize: '13px',
        fontWeight: 600,
        color: unoTheme.mainColor,
        marginBottom: '2px',
        textAlign: 'left',
    },
    hint: {
        fontSize: '12px',
        color: unoTheme.secondaryColor,
        marginBottom: '12px',
    },
    sectionTitle: {
        fontSize: '14px',
        fontWeight: 600,
        color: unoTheme.defaultTextColor,
        marginTop: '24px',
        marginBottom: '2px',
    },
    blockedBox: {
        border: 'solid 1px ' + unoTheme.warningColor,
        background: '#FFF9E6',
        borderRadius: '6px',
        padding: '12px 16px',
    },
    blockedTitle: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        fontWeight: 600,
        fontSize: '13px',
        marginBottom: '6px',
    },
    blockedItem: {
        fontSize: '12px',
        marginLeft: '26px',
        listStyle: 'disc',
    },
});

/**
 * Etapa 2 — Documentos.
 *
 * Duas seções, ambas vindas do catálogo TRADEMATE_DOCUMENTS do backend:
 *
 * - os documentos gerados pelo UNO, um por linha. O fluxo é sempre o mesmo: gerar o .docx,
 *   baixar, assinar fora do sistema e anexar o assinado. Alterar a etapa 1 zera gerado e assinado
 *   dos documentos afetados — o backend faz isso na mesma transação do save;
 * - os documentos complementares (`complementary`), que o UNO não gera: o RPPS só anexa o arquivo
 *   (PDF, JPG ou PNG, até 5 MB). RG e CPF aceitam vários arquivos; um já atende a linha.
 *
 * O envio só libera quando todos estiverem atendidos (ver isDocumentFulfilled).
 */
export default function TrademateStep2Documents(props) {

    const { canEdit, documents, rppsPendencies, sendPendencies, onChanged } = props;

    const classes = useStyles();
    const dispatch = useDispatch();

    const fileInputRef = useRef(null);
    const complementaryInputRef = useRef(null);
    const [fileValue, setFileValue] = useState('');
    const [complementaryFileValue, setComplementaryFileValue] = useState('');
    const [documentIdToUpload, setDocumentIdToUpload] = useState(null);

    const generatedDocuments = documents.filter(d => !d.complementary);
    const complementaryDocuments = documents.filter(d => d.complementary);

    const alert = (type, message) => dispatch(allActions.mainAlertActions.setMainAlert({
        show: true, type, message
    }));

    const errorMessage = (body, fallback) => (body && body.message ? body.message : fallback);

    //Se há mais pendências do que documentos não atendidos, sobrou algo que não é de documento
    const unfulfilledCount = documents.filter(d => !isDocumentFulfilled(d)).length;
    const hasOtherPendencies = sendPendencies && sendPendencies.length > unfulfilledCount;

    const withLoad = async (loadMessage, action) => {
        dispatch(allActions.innerLoadActions.setInnerLoad({ show: true, loadMessage }));
        const response = await action();
        dispatch(allActions.innerLoadActions.setInnerLoad({ show: false }));
        return response;
    };

    const doGenerate = async (document) => {

        const response = await withLoad('Gerando documento...',
            () => TrademateAPI.generateDocument(document.id));

        if (response.success) {
            alert('success', 'Documento gerado');
            onChanged();
        } else {
            alert('error', errorMessage(response.body, 'Erro ao gerar o documento'));
        }
    };

    /**
     * @param {'generated'|'signed'|'file'} type
     * @param {object} [file] o arquivo do complementar, quando type = 'file'
     */
    const doDownload = async (document, type, file) => {

        const response = await withLoad('Preparando download...',
            () => TrademateAPI.getDocumentUrl(document.id, type, file ? file.id : undefined));

        if (response.success) {
            Object.assign(window.document.createElement('a'), {
                target: '_blank',
                href: response.body.presignedUrl,
            }).click();
        } else {
            alert('error', errorMessage(response.body, 'Erro ao baixar o documento'));
        }
    };

    const handleUploadClick = (document) => {
        setDocumentIdToUpload(document.id);
        fileInputRef.current.click();
    };

    //`multiple` é ligado direto no elemento, e não por estado: o click() precisa sair neste mesmo
    //handler (o navegador só abre o seletor dentro do gesto do usuário), antes de qualquer render
    const handleComplementaryUploadClick = (document) => {
        setDocumentIdToUpload(document.id);
        complementaryInputRef.current.multiple = !!document.multiple_files;
        complementaryInputRef.current.click();
    };

    const handleFileSelected = async (event) => {

        const file = event.target.files[0];
        //Reset para permitir escolher o mesmo arquivo de novo
        setFileValue('');

        if (!file || !documentIdToUpload) return;

        const response = await withLoad('Enviando arquivo...',
            () => TrademateAPI.uploadSignedDocument(documentIdToUpload, file));

        setDocumentIdToUpload(null);

        if (response.success) {
            alert('success', 'Documento assinado anexado');
            onChanged();
        } else {
            alert('error', errorMessage(response.body, 'Erro ao enviar o arquivo'));
        }
    };

    const handleComplementaryFilesSelected = async (event) => {

        const files = Array.from(event.target.files ? event.target.files : []);
        setComplementaryFileValue('');

        if (files.length === 0 || !documentIdToUpload) return;

        const response = await withLoad(
            files.length > 1 ? 'Enviando arquivos...' : 'Enviando arquivo...',
            () => TrademateAPI.uploadComplementaryFiles(documentIdToUpload, files));

        setDocumentIdToUpload(null);

        if (response.success) {
            alert('success', files.length > 1 ? 'Arquivos anexados' : 'Arquivo anexado');
            onChanged();
        } else {
            alert('error', errorMessage(response.body, 'Erro ao enviar o arquivo'));
            //Com vários arquivos, os anteriores ao que falhou já subiram: recarrega para a linha
            //mostrar o que de fato ficou anexado
            if (files.length > 1) onChanged();
        }
    };

    const doRemoveSigned = async (document) => {

        const confirmed = window.confirm('Remover o documento assinado anexado?');
        if (!confirmed) return;

        const response = await withLoad('Removendo...',
            () => TrademateAPI.deleteSignedDocument(document.id));

        if (response.success) {
            alert('success', 'Anexo removido');
            onChanged();
        } else {
            alert('error', errorMessage(response.body, 'Erro ao remover o anexo'));
        }
    };

    const doRemoveFile = async (file) => {

        const confirmed = window.confirm('Remover o arquivo ' + file.file_name + '?');
        if (!confirmed) return;

        const response = await withLoad('Removendo...',
            () => TrademateAPI.deleteDocumentFile(file.id));

        if (response.success) {
            alert('success', 'Arquivo removido');
            onChanged();
        } else {
            alert('error', errorMessage(response.body, 'Erro ao remover o arquivo'));
        }
    };

    //R4: os documentos gerados são preenchidos com dados do cadastro do RPPS e do Gestor de
    //Recursos, que não são editáveis aqui. Faltando qualquer um, o documento sairia com lacunas e
    //seria assinado assim — então a seção dos gerados fica bloqueada até o cadastro ser completado.
    //Os complementares não leem nada disso e continuam disponíveis.
    const renderRppsBlocked = () => (
        <div className={classes.blockedBox}>
            <div className={classes.blockedTitle}>
                <WarningIcon style={{ color: unoTheme.warningColorStrong, fontSize: '18px' }} />
                {'Complete o cadastro do RPPS para gerar os documentos'}
            </div>
            <ul>
                {rppsPendencies.map((pendency, index) => (
                    <li key={'rpps-pendency-' + index} className={classes.blockedItem}>{pendency}</li>
                ))}
            </ul>
            <Typography className={classes.hint} style={{ marginTop: '8px', marginBottom: 0 }}>
                {'Esses dados vêm de Cadastros › Clientes e do cadastro do gestor, não desta tela.'}
            </Typography>
        </div>
    );

    const renderGeneratedDocuments = () => (
        <>
            <Alert severity="info" className={classes.instructions}>
                <AlertTitle className={classes.instructionsTitle}>
                    {'Baixe, confira, assine e envie de volta.'}
                </AlertTitle>
                {'Os documentos abaixo já vêm preenchidos com os dados deste RPPS no UNO. '
                    + 'Baixe cada um, colha as assinaturas dos gestores indicados e faça o '
                    + 're-upload do arquivo assinado.'}
            </Alert>

            {/* O contador no rodapé já diz quantos documentos faltam; aqui só entram as
                pendências de outra natureza — gestor desativado, Gestor de Recursos trocado. A
                detecção é por contagem, para não depender de casar o texto da mensagem. */}
            {hasOtherPendencies && (
                <Alert severity="warning" className={classes.instructions}>
                    <AlertTitle className={classes.instructionsTitle}>
                        {'Pendências que impedem o envio'}
                    </AlertTitle>
                    <ul>
                        {sendPendencies.map((pendency, index) => (
                            <li key={'send-pendency-' + index} className={classes.blockedItem}>
                                {pendency}
                            </li>
                        ))}
                    </ul>
                </Alert>
            )}

            {generatedDocuments.map((document) => (
                <TrademateDocumentRow
                    key={document.document_code}
                    document={document}
                    canEdit={canEdit}
                    onGenerate={() => doGenerate(document)}
                    onDownloadGenerated={() => doDownload(document, 'generated')}
                    onUploadSigned={() => handleUploadClick(document)}
                    onDownloadSigned={() => doDownload(document, 'signed')}
                    onRemoveSigned={() => doRemoveSigned(document)}
                />
            ))}
        </>
    );

    const isRppsBlocked = rppsPendencies && rppsPendencies.length > 0;

    return (
        <div>

            {isRppsBlocked ? renderRppsBlocked() : renderGeneratedDocuments()}

            {complementaryDocuments.length > 0 && (
                <>
                    <Typography align="left" className={classes.sectionTitle}>
                        {'Documentos complementares'}
                    </Typography>
                    <Typography align="left" className={classes.hint}>
                        {'Não são gerados pelo UNO: anexe o arquivo em PDF, JPG ou PNG, com até 5 MB. '
                            + 'RG e CPF aceitam mais de um arquivo (ex.: frente e verso).'}
                    </Typography>

                    {complementaryDocuments.map((document) => (
                        <TrademateComplementaryDocumentRow
                            key={document.document_code}
                            document={document}
                            canEdit={canEdit}
                            onUpload={() => handleComplementaryUploadClick(document)}
                            onDownloadFile={(file) => doDownload(document, 'file', file)}
                            onRemoveFile={doRemoveFile}
                        />
                    ))}
                </>
            )}

            {/* O gerado agora é .docx, então o assinado pode voltar em PDF (escaneado ou assinado
                digitalmente) ou no próprio .docx — a whitelist do s3Service aceita os dois */}
            <input
                type="file"
                name="signedDocument"
                accept=".pdf,.docx"
                value={fileValue}
                onChange={handleFileSelected}
                ref={fileInputRef}
                hidden
            />

            {/* Complementares: só PDF e imagem. O `accept` só filtra o seletor — o formato e o
                tamanho são validados de verdade no upload (API e backend) */}
            <input
                type="file"
                name="complementaryDocument"
                accept=".pdf,.jpg,.jpeg,.png"
                value={complementaryFileValue}
                onChange={handleComplementaryFilesSelected}
                ref={complementaryInputRef}
                hidden
            />

        </div>
    );
}
